
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Step1Documents from './components/Step1Documents';
import Step2PersonalInfo from './components/Step2PersonalInfo';
import ProgressBar from './components/ProgressBar';
import type { UploadsResponse } from '../../shared/models/UploadsResponse';

export default function Processo() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formData, setFormData] = useState({
    // Etapa 1
    nomeProcesso: '',
    nomeCompleto: '',
    cpf: '',
    dataNascimento: '',
    endereco: '',
    telefone: '',
    // Etapa 2
    documentoIdentificacao: null as File | null,
    comprovanteResidencia: null as File | null,
    comprovanteRenda: null as File | null,
  });

  const [uploads, setUploads] = useState<UploadsResponse | null>(null);

  const steps = [
    { number: 1, title: 'Documentos', description: 'Upload de arquivos' },
    { number: 2, title: 'Dados Pessoais', description: 'Informações básicas' },
  ];

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleDataChange = (data: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleUploadsComplete = (uploads: UploadsResponse) => {
    setUploads(uploads);
  };

  const handleSubmit = () => {
    // Mostrar mensagem de sucesso
    setShowSuccessMessage(true);
    
    // Redirecionar após 3 segundos
    setTimeout(() => {
      navigate('/habita/pastas');
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Success Message Overlay */}
      {showSuccessMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-double-line text-3xl text-green-600"></i>
            </div>
            <h3 className="text-2xl font-fraunces font-bold text-textPrimary mb-4">
              Processo Enviado com Sucesso!
            </h3>
            <p className="font-instrument text-textSecondary mb-6">
              Seu processo habitacional foi registrado e está sendo analisado pela nossa equipe. 
              Você receberá atualizações sobre o andamento em breve.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="font-instrument text-blue-800 text-sm">
                <strong>Próximos passos:</strong> Acompanhe o status do seu processo em Pastas.
                Nossa equipe entrará em contato se precisar de documentos adicionais.
              </p>
            </div>
            <div className="flex items-center justify-center space-x-2 text-textSecondary">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              <span className="font-instrument text-sm">Redirecionando para Pastas...</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[3000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-fraunces font-bold text-textPrimary mb-2">
              Novo Processo
            </h1>
            <p className="font-instrument text-textSecondary">
              Preencha as informações solicitadas para iniciar seu processo habitacional
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              to="/dashboard"
              className="text-primary hover:text-primary/80 font-instrument font-medium cursor-pointer"
            >
              ← Voltar ao Dashboard
            </Link>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <ProgressBar steps={steps} currentStep={currentStep} />
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {currentStep === 1 && (
            <Step1Documents
              data={formData}
              onChange={handleDataChange}
              onUploadsComplete={handleUploadsComplete}
            />
          )}

          {currentStep === 2 && (
            <Step2PersonalInfo
              data={formData}
              onChange={handleDataChange}
              onNext={handleSubmit}
              onPrevious={handlePrevious}
              uploads={uploads!}
            />
          )}
        </div>
      </div>
    </div>
  );
}
